// ====================================================================
// ziven-core — 跨扩展共享上传方法 (Phase D1)
// ====================================================================
//
// 别的扩展这样用 (import 路径见下方「ext: 命名空间」一节):
//
//   import { uploadFile } from 'ext:ziiven/ziven-core/common/helpers/uploadFile';
//
//   const url = await uploadFile(file, { prefix: 'scheduler_contract' });
//   composer.body = `[img]${url}[/img]`;
//
// 想要 key (后续要 deleteFile / 要入库) 用 uploadFileResult():
//
//   const { url, key, provider } = await uploadFileResult(file, { prefix: 'seedream' });
//
// ────────────────────────────────────────────────────────────────────
// 为什么要有这个共享方法
// ────────────────────────────────────────────────────────────────────
// 全站有 7 个用户端直传点 (ziven-scheduler ×3 / ziven-custom-tags ×1 /
// ziven-dress-up ×2 / ziven-core 自己的 HeroImageCropModal ×1), 它们在
// Phase A 之前全部**硬编码 COS**: 拿签名 → 拼 key → FormData → XHR。
// Phase A 之后后端支持 cos / qiniu / local 三档, 而 `generateSignature()` 的
// **返回结构随 provider 变** (见下表), 于是 7 个点各自再写一遍 provider 分支
// 就是 7 份会各自腐烂的分叉。本模块就是把分支**收进一个文件**。
//
//   | provider | 签名返回结构                              | 直传? |
//   |----------|-------------------------------------------|--------|
//   | cos      | q_ak/q_key_time/signature/policy/bucket/... | 是     |
//   | qiniu    | {provider,token,bucket,key_prefix,up_host}  | 是     |
//   | local    | {provider,direct_upload:false,upload_endpoint,max_bytes} | 否,走后端中转 |
//
// 本模块是 **Phase D 的基础能力**: 只建方法, **本轮不迁移任何调用方**
// (迁移是 Phase D2 的活)。所以 7 个点现在仍然走各自的老路径, 行为不变。
//
// ────────────────────────────────────────────────────────────────────
// 🔴 ext: 命名空间 — 路径写法 (实测确认, 别照抄错版本)
// ────────────────────────────────────────────────────────────────────
//   `flarum-webpack-config` 与运行期 `ExportRegistry` 用的是**同一套正则**:
//
//     /^ext:([^\/]+)\/(?:flarum-(?:ext-)?)?([^\/]+)(?:\/(.+))?$/
//     → namespace = `${group}-${package}`, id = 其余路径
//
//   composer.json 的 name 是 `ziiven/ziven-core` ⇒ namespace `ziiven-ziven-core`。
//   所以**正确写法是**:
//
//     ✅ ext:ziiven/ziven-core/common/helpers/uploadFile
//     ✅ ext:ziiven/flarum-ext-ziven-core/common/helpers/uploadFile
//     ❌ ext:ziiven-ziven-core/common/helpers/uploadFile
//        ← 会解析成 namespace `ziiven-ziven-core-ziiven-ziven-core` (多拼一段), 取不到
//
// ────────────────────────────────────────────────────────────────────
// 🔴 运行期 reg.get() 失败只 warn 不抛 ⇒ 必须自己兜底
// ────────────────────────────────────────────────────────────────────
// `ExportRegistry.get()` 在模块没注册时: `flarum.debug` 开 → throw;
// 关 → `console.warn` + **返回 undefined**。后者会让调用方在别处炸成
// 「xxx is not a function」, 排查成本极高。本模块内部所有依赖都在用之前
// 显式校验并抛**可读**错误; 调用方那边也请加一层 (见文末「调用方兜底」)。
//
// ────────────────────────────────────────────────────────────────────
// 各家表单字段顺序 (错了就是 403, 本模块内部已按要求固定)
// ────────────────────────────────────────────────────────────────────
//   - COS: 参与签名的 7 个字段必须**严格字典序**。
//     本模块用 `Object.keys(fields).sort()` 生成 (不是手背下来的顺序), 所以
//     以后有人往 fields 里加字段也自动排好。`file` 固定放最后 —— 它不在
//     policy conditions 里 (CosStorageBackend::generateSignature 只签 q-ak /
//     q-key-time / q-sign-algorithm / q-sign-time), 位置不影响签名校验, 且
//     S3/COS POST 惯例与现存 7 个调用点都是 file 收尾。
//   - 七牛: 只有 `token` / `key` / `file` 三个字段, 无顺序要求。
//   - 本地: multipart 交给后端, 顺序无关。
// ====================================================================

import app from 'flarum/common/app';

/** 默认 key 前缀 (调用方几乎都会覆盖它) */
export const DEFAULT_KEY_PREFIX = 'shared';

/** 签名端点 (CloudStorage 的 provider 是 cos 时也走这个) */
export const SIGNATURE_ENDPOINT = '/api/ziven/core/cos/signature';

/** 非敏感配置端点 (七牛拿访问域名用; COS 分支已从签名里拿到够用的字段) */
export const CONFIG_ENDPOINT = '/api/ziven/core/cos/config';

/**
 * @typedef {Object} UploadOptions
 * @property {string}   [prefix='shared'] 存储 key 前缀, 如 'scheduler_lesson_review'。
 *                                    各调用方继续用自己原有的前缀, 本方法不写死。
 * @property {string}   [fileName]        覆盖文件名 (云端档用它拼 key; 本地档只用它的扩展名)。
 * @property {number}   [expire=3600]     签名有效期 (秒)。后端会夹到 [300, 21600]。
 * @property {Function} [onProgress]      (loadedBytes, totalBytes) => void, 仅云端直传有。
 * @property {Object}   [extraFields]     额外表单字段 (会插在 `file` 之前)。
 * @property {number}   [timeout=0]       XHR 超时 (ms), 0 = 不设。
 */

/**
 * @typedef {Object} UploadResult
 * @property {boolean} success 恒为 true (失败一律 throw)
 * @property {string}  url     **绝对 URL**, 可直接塞进 <img src> / API 响应
 * @property {string}  key     存储 key (不含 local_path / bucket)。后续 deleteFile() 用它。
 * @property {string}  provider 'cos' | 'qiniu' | 'local'
 * @property {number}  size    文件字节数
 */

// ── 内部 ──────────────────────────────────────────────────────────

/**
 * 兜底: 确认 Flarum 的 app 单例可用。
 * `import app from 'flarum/common/app'` 也是走 reg.get('core', ...),
 * 万一 core 没就绪, 这里给出**可读**错误而不是让调用方炸在别处。
 */
function requireApp() {
  if (!app || typeof app.request !== 'function') {
    throw new Error('[ziven-core] uploadFile: Flarum core 的 app 模块未就绪 ' + '(flarum.reg.get("core", "common/app") 返回空)。' + '通常意味着 ziven-core 的 JS 资源没加载 —— 检查后台「扩展」页 ziven-core 是否启用, ' + '以及改过 js/dist 后有没有跑 php flarum cache:clear。');
  }
  return app;
}

/**
 * 后端未启用时各 provider 的错误文案 → 人话。
 * (CosSignatureController 对未启用的档会直接回 `['error' => '... not enabled']`)
 */
function friendlySignatureError(sig) {
  const raw = String(sig.error || '');
  if (/local storage not enabled/i.test(raw)) {
    return '本地服务器存储未启用, 请在 ziven-core 后台「存储平台」中启用「本地服务器」。';
  }
  if (/cos not enabled/i.test(raw)) {
    return '腾讯云 COS 未启用或配置不完整, 请在 ziven-core 后台「存储平台」中检查 COS 配置。';
  }
  if (/qiniu not enabled/i.test(raw)) {
    return '七牛云未启用或配置不完整, 请在 ziven-core 后台「存储平台」中检查七牛配置。';
  }
  return raw || '获取上传签名失败';
}

/**
 * 取签名。**结构随 provider 变**, 所以这里把三档归一成
 * `{ provider, directUpload, maxBytes }` + 原始签名, 供下面分支使用。
 */
async function fetchSignature(expire) {
  const flarumApp = requireApp();
  const resp = await flarumApp.request({
    method: 'GET',
    url: SIGNATURE_ENDPOINT,
    params: {
      expire
    }
  });
  if (!resp || !resp.success || !resp.data) {
    throw new Error('[ziven-core] uploadFile: 获取上传签名失败: ' + (resp && resp.error || '未知错误'));
  }
  const sig = resp.data;
  if (sig.error) {
    throw new Error('[ziven-core] uploadFile: ' + friendlySignatureError(sig));
  }

  // ⚠️ COS / 七牛 签名里**没有** provider 字段时, 按 cos 处理 ——
  //    跟 CosStorageBackend 逐字节兼容 (Phase A 冻结了它的返回结构, 不能加字段)。
  const provider = sig.provider === 'qiniu' ? 'qiniu' : sig.provider === 'local' ? 'local' : 'cos';
  return {
    provider,
    // 本地档恒为 false (LocalStorageBackend 不下发任何可写凭据)
    directUpload: provider === 'local' ? false : sig.direct_upload !== false,
    maxBytes: typeof sig.max_bytes === 'number' ? sig.max_bytes : null,
    sig
  };
}

/** 生成 `{prefix}/{ts}_{rand}.{ext}` 形态的 key (跟现存 7 个调用点同款) */
function buildKey(prefix, fileName) {
  const ts = Date.now();
  const rand = Math.random().toString(36).slice(2, 12);
  const parts = String(fileName || '').split('.');
  const ext = (parts.length > 1 ? parts.pop() : 'bin').toLowerCase().replace(/[^a-z0-9]/g, '') || 'bin';
  const p = String(prefix || DEFAULT_KEY_PREFIX).replace(/^\/+|\/+$/g, '') || DEFAULT_KEY_PREFIX;
  return "".concat(p, "/").concat(ts, "_").concat(rand, ".").concat(ext);
}

/** XHR 上传 (云端直传)。保留 progress —— 7 个调用点里好几个要显示进度条。 */
function xhrUpload(url, formData, options) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    if (options.timeout > 0) {
      xhr.timeout = options.timeout;
    }
    if (typeof options.onProgress === 'function' && xhr.upload) {
      xhr.upload.onprogress = e => {
        if (e.lengthComputable) {
          options.onProgress(e.loaded, e.total);
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error("[ziven-core] uploadFile: \u4E0A\u4F20\u5931\u8D25 HTTP ".concat(xhr.status) + (xhr.responseText ? " \u2014 ".concat(String(xhr.responseText).slice(0, 300)) : '')));
    };
    xhr.onerror = () => reject(new Error('[ziven-core] uploadFile: 上传网络错误'));
    xhr.ontimeout = () => reject(new Error('[ziven-core] uploadFile: 上传超时'));
    xhr.onabort = () => reject(new Error('[ziven-core] uploadFile: 上传已取消'));
    xhr.send(formData);
  });
}

/**
 * COS 分支 —— 🔴 表单字段顺序 = 403 的头号原因, 严格字典序。
 *
 * 顺序由 `Object.keys().sort()` 生成 (不是手写死), 所以以后加字段自动排好。
 * 产出的顺序: key → policy → q-ak → q-key-time → q-sign-algorithm →
 *             q-sign-time → q-signature, 最后 file。
 */
function buildCosFormData(file, cosKey, sig) {
  const keyTime = sig.q_key_time || sig.key_time;
  const fields = {
    key: cosKey,
    // CosStorageBackend 固定返 'sha1'; 老调用点也写死 'sha1'。两者一致, 取签名的值优先。
    'q-ak': sig.q_ak,
    'q-key-time': keyTime,
    'q-sign-algorithm': sig.q_sign_algorithm || 'sha1',
    // 后端不单独返 q_sign_time —— policy conditions 里它跟 q_key_time 同值 (见 CosStorageBackend)
    'q-sign-time': keyTime,
    policy: sig.policy,
    'q-signature': sig.signature
  };

  // 缺字段就是签名不完整, 早失败好过让 COS 回 403
  for (const name of Object.keys(fields)) {
    if (fields[name] === undefined || fields[name] === null || fields[name] === '') {
      throw new Error("[ziven-core] uploadFile: COS \u7B7E\u540D\u7F3A\u5C11\u5B57\u6BB5 \"".concat(name, "\", \u8BF7\u68C0\u67E5 ziven-core \u7684 COS \u914D\u7F6E"));
    }
  }
  const formData = new FormData();
  Object.keys(fields).sort() // 🔴 字典序 —— 顺序错了 COS 直接 403
  .forEach(name => formData.append(name, fields[name]));

  // 🔴 file 必须最后 (见文件头注释: 它不在 policy conditions 里, 且各家惯例都是收尾)
  formData.append('file', file, cosKey.split('/').pop());
  return {
    formData,
    baseUrl: cosBaseUrl(sig)
  };
}

/** COS 上传域名: 优先 CDN, 否则 bucket-appid.region */
function cosBaseUrl(sig) {
  if (sig.cdn_domain) {
    return "https://".concat(String(sig.cdn_domain).replace(/\/$/, ''), "/");
  }
  return "https://".concat(sig.bucket, "-").concat(sig.app_id, ".cos.").concat(sig.region, ".myqcloud.com/");
}

/** 七牛分支 —— 单个 token 字段, 无顺序要求 */
function buildQiniuFormData(file, qiniuKey, sig) {
  const formData = new FormData();
  formData.append('token', sig.token);
  formData.append('key', qiniuKey);
  formData.append('file', file);
  return formData;
}

/**
 * 本地分支 —— POST 给后端中转路由, 由后端落盘。
 *
 * 用 `app.request` 而不是裸 XHR: 它自动带 X-CSRF-Token (Flarum 的
 * CheckCsrfToken 中间件拦 POST), 并且把非 2xx 响应变成带 status 的
 * RequestError —— 本地档的错误全在后端 (503 未启用 / 413 超限 / 400 消毒失败),
 * 那些文案要原样透传给用户, 所以这条路径**必须**能读到后端返回体。
 */
async function uploadViaBackend(file, options) {
  const flarumApp = requireApp();
  const formData = new FormData();
  formData.append('file', file, options.fileName || file.name);
  // prefix 只传意图, key 由服务端生成 + 消毒 (见 LocalStorageUploadController 类注释)
  formData.append('prefix', options.prefix || DEFAULT_KEY_PREFIX);
  const endpoint = (options.uploadEndpoint || 'api/ziven/core/storage/upload').replace(/^\/+/, '/');
  let resp;
  try {
    resp = await flarumApp.request({
      method: 'POST',
      url: endpoint,
      body: formData,
      deserialize: text => {
        try {
          return JSON.parse(text);
        } catch (e) {
          throw new Error("[ziven-core] uploadFile: \u540E\u7AEF\u8FD4\u56DE\u7684\u4E0D\u662F JSON \u2014 ".concat(String(text).slice(0, 200)));
        }
      }
    });
  } catch (e) {
    throw new Error("[ziven-core] uploadFile: ".concat(extractBackendError(e)), e);
  }
  if (!resp || !resp.success || !resp.url) {
    throw new Error('[ziven-core] uploadFile: ' + (resp && resp.error || '后端返回缺少 url'));
  }
  return {
    url: resp.url,
    key: resp.key,
    provider: resp.provider || 'local'
  };
}

/**
 * 从 `app.request` 抛出的对象里挖出后端那条**可读原因**。
 *
 * 🔴 Flarum 2.0 的 `RequestError` **不是 Error 子类** —— 它没有 `.message`,
 *    真正有用的东西在 `.response`(已解析的对象) / `.responseText`(原始字符串)。
 *    所以 `catch (e) { e.message }` 拿到的会是 undefined, 最后掉进「未知错误」。
 *    (实测踩过: 503 未启用 → 用户看到 "[ziven-core] uploadFile: 未知错误"。)
 *
 * 另外本端点返回的是自定义 `{success, error}` 信封, 不是 Flarum 标准
 * `{errors:[{detail}]}` —— 两种都认, 免得以后后端改信封格式就断链。
 */
function extractBackendError(e) {
  if (!e) {
    return '未知错误';
  }

  // 1) 标准 Error (含我们自己抛的)
  if (typeof e.message === 'string' && e.message) {
    return e.message;
  }

  // 2) RequestError.response —— 已是对象, **不要再 JSON.parse**
  const body = e.response !== undefined && e.response !== null ? e.response : safeParse(e.responseText);
  if (body && typeof body === 'object') {
    if (body.error) {
      return String(body.error);
    }
    if (Array.isArray(body.errors) && body.errors.length && body.errors[0].detail) {
      return String(body.errors[0].detail);
    }
  }

  // 3) 兜底: 原始文本
  if (typeof e.responseText === 'string' && e.responseText) {
    return String(e.responseText).slice(0, 200);
  }
  if (typeof e.status === 'number') {
    return "\u4E0A\u4F20\u5931\u8D25 (HTTP ".concat(e.status, ")");
  }
  return '未知错误';
}
function safeParse(text) {
  try {
    return JSON.parse(text);
  } catch (e) {
    return null;
  }
}

/** 字节数 → 人话 (上限配成 1KB 时 %.2f MB 会打印成「0.00 MB」, 等于没说话) */
function formatBytes(bytes) {
  if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(2) + ' GB';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(2) + ' MB';
  if (bytes >= 1024) return Math.round(bytes / 1024) + ' KB';
  return bytes + ' B';
}

/** 内部统一入口 —— 三个分支都在这一个文件里 */
async function runUpload(file, options) {
  if (!file) {
    throw new Error('[ziven-core] uploadFile: 缺少文件参数');
  }
  const opts = {
    prefix: options.prefix || DEFAULT_KEY_PREFIX,
    fileName: options.fileName,
    expire: options.expire || 3600,
    onProgress: options.onProgress,
    extraFields: options.extraFields,
    timeout: options.timeout || 0,
    uploadEndpoint: options.uploadEndpoint
  };
  const _await$fetchSignature = await fetchSignature(opts.expire),
    provider = _await$fetchSignature.provider,
    directUpload = _await$fetchSignature.directUpload,
    maxBytes = _await$fetchSignature.maxBytes,
    sig = _await$fetchSignature.sig;

  // 本档配置的封顶 (local_max_size / 未来其它档的 max_bytes) —— 超了**客户端先拦**,
  // 省一次无谓的往返。服务端仍会再拦一次 (以服务端为准)。
  if (maxBytes !== null && typeof file.size === 'number' && file.size > maxBytes) {
    throw new Error("[ziven-core] uploadFile: \u6587\u4EF6\u8FC7\u5927 (".concat(formatBytes(file.size), "), \u8D85\u8FC7\u5F53\u524D\u4E0A\u9650 ").concat(formatBytes(maxBytes)));
  }

  // ── 分支 1: 本地 → 后端中转 ────────────────────────────────────
  if (provider === 'local' || directUpload === false) {
    const result = await uploadViaBackend(file, opts);
    return {
      success: true,
      url: result.url,
      key: result.key,
      provider: result.provider,
      size: file.size || 0
    };
  }

  // ── 分支 2: 七牛 → 浏览器直传 ──────────────────────────────────
  if (provider === 'qiniu') {
    const qiniuKey = "".concat((sig.key_prefix || opts.prefix).replace(/\/$/, ''), "/").concat(Date.now(), "_").concat(Math.random().toString(36).slice(2, 12), ".").concat((String(opts.fileName || file.name).split('.').pop() || 'bin').toLowerCase());
    const formData = buildQiniuFormData(file, qiniuKey, sig);
    applyExtraFields(formData, opts.extraFields);
    await xhrUpload("https://".concat(sig.up_host, "/"), formData, opts);

    // 访问域名在 getUploadConfig() 里 (generateSignature 不带), 所以补拉一次
    const cfgResp = await requireApp().request({
      method: 'GET',
      url: CONFIG_ENDPOINT
    });
    const cfg = cfgResp && cfgResp.success && cfgResp.data ? cfgResp.data : {};
    const base = cfg.domain || "https://".concat(sig.bucket, ".qiniudn.com");
    return {
      success: true,
      url: "".concat(String(base).replace(/\/$/, ''), "/").concat(qiniuKey),
      key: qiniuKey,
      provider: 'qiniu',
      size: file.size || 0
    };
  }

  // ── 分支 3: COS → 浏览器直传 (存量默认路径) ────────────────────
  const cosKey = buildKey(opts.prefix, opts.fileName || file.name);
  const _buildCosFormData = buildCosFormData(file, cosKey, sig),
    formData = _buildCosFormData.formData,
    baseUrl = _buildCosFormData.baseUrl;
  applyExtraFields(formData, opts.extraFields);
  await xhrUpload(baseUrl, formData, opts);
  return {
    success: true,
    url: baseUrl + cosKey,
    key: cosKey,
    provider: 'cos',
    size: file.size || 0
  };
}

/** extraFields 插在 file 之前 (file 必须是最后一个, 见文件头注释) */
function applyExtraFields(formData, extraFields) {
  if (!extraFields || typeof extraFields !== 'object') {
    return;
  }
  Object.keys(extraFields).sort().forEach(k => formData.append(k, extraFields[k]));
}

// ── 对外 API ──────────────────────────────────────────────────────

/**
 * 上传一个文件, 无论当前是 COS / 七牛 / 本地。
 *
 * 调用方只需要关心「文件 + 想要的前缀」, 不需要知道当前是哪个存储平台。
 *
 * 🔴 这里**必须写成 `export function` 而不是 `export async function`** ——
 *   `flarum-webpack-config` 的 autoExportLoader 用一条正则扫导出名来决定
 *   `flarum.reg.add(...)` 里登记哪些名字:
 *
 *     /export\s+?(?:\* as|function|{\s*(...)+|const|let|abstract\s?|class)+?\s?([A-Za-z_]*)?/g
 *
 *   `export async function` 中间的 `async` **不在候选里, 整行匹配不上** →
 *   该函数**不会被登记进 registry**, 而 `reg.get()` 找不到时是 **console.warn +
 *   返 undefined**(debug 模式才 throw) ⇒ 调用方拿到 undefined, 报成
 *   「uploadFile is not a function」, 跟真实原因毫无关系。
 *   实测过: 写成 `export async function` 时 bundle 里的登记 map 只有
 *   `{DEFAULT_KEY_PREFIX, SIGNATURE_ENDPOINT, CONFIG_ENDPOINT}`, 两个函数不见了。
 *   改成 `export function` + 内部 `async` 辅助函数之后才正常登记。
 *
 * @param {File|Blob} file
 * @param {UploadOptions} [options]
 * @returns {Promise<string>} **可直接访问的绝对 URL** (三档统一)
 * @throws {Error} 未启用 / 超限 / 路径被拒 / 网络失败 —— error.message 是可直接显示的中文
 */
export function uploadFile(file, options) {
  if (options === void 0) {
    options = {};
  }
  return runUpload(file, options).then(result => result.url);
}

/**
 * 同 uploadFile(), 但返回完整结果 (需要 key 时用这个)。
 *
 * 同样**必须** `export function` 而非 `export async function` (理由见上)。
 *
 * @param {File|Blob} file
 * @param {UploadOptions} [options]
 * @returns {Promise<UploadResult>}
 */
export function uploadFileResult(file, options) {
  if (options === void 0) {
    options = {};
  }
  return runUpload(file, options);
}

// ────────────────────────────────────────────────────────────────────
// 📌 调用方兜底 (Phase D2 迁移那 7 个点时请照抄)
// ────────────────────────────────────────────────────────────────────
//   import { uploadFile } from 'ext:ziiven/ziven-core/common/helpers/uploadFile';
//   if (typeof uploadFile !== 'function') {
//     // ziven-core 没启用 / 资源没加载 —— 给用户一句人话, 不要让代码炸在别处
//     throw new Error('ziven-core 上传组件未就绪, 请刷新页面后重试');
//   }
//
// 理由: `flarum.reg.get()` 找不到模块时, debug 模式 throw、正常模式 **console.warn
// + 返 undefined**。后一种情况下你拿到的是 undefined, 报错信息是
// 「uploadFile is not a function」, 跟真实原因 (ziven-core 没装/没启用) 毫无关系。
// ====================================================================
flarum.reg.add('ziiven-ziven-core', 'common/helpers/uploadFile', { DEFAULT_KEY_PREFIX: DEFAULT_KEY_PREFIX,SIGNATURE_ENDPOINT: SIGNATURE_ENDPOINT,CONFIG_ENDPOINT: CONFIG_ENDPOINT,uploadFile: uploadFile,uploadFileResult: uploadFileResult, });